import { Button, Spin, TextLink } from '@bfe/moly';
import BybitIcon from '@/assets/svg/home/bybit_logo.svg';
import BybitIconSmall from '@/assets/svg/home/Bybit_logo_small.svg';
import { FundDistributionPieChart } from '@/containers/home/hero/fund-distribution-chart';
import { useRequest } from 'ahooks';
import { getHackTotalStats } from '@/services/home';
import { formatNumber, appPath } from '@web3/base-utils';
import { useParams } from '@web3/base-hooks';
import { useRouter } from 'next/router';
import { scTrack } from '@web3/base-tracking';
import { Event, Page } from '@/config/track-config';
import { useCallback, useMemo, useRef } from 'react';
import BigNumber from 'bignumber.js';
import { useFm } from '@web3/base-hooks';
import { TRACKING_API_LINK, VIEW_RULES_LINK, BYBIT_LINK } from '@/config';
import { useWindowScroll } from 'react-use';
import clsx from 'clsx';
import { CommonLoading } from '@/components/CommonLoading';

export const Hero = () => {
  const fm = useFm();
  const { data: res, loading } = useRequest(getHackTotalStats);
  const router = useRouter();
  const { locale } = useParams();
  const totalStats = useMemo(() => res?.result, [res]);
  const footerActionRef = useRef<HTMLDivElement>(null);
  const { y } = useWindowScroll();

  // 写死
  const totalHackedAmount = 1_400_000_000;
  const totalBounty = 140_000_000;

  const handleSubmitReport = () => {
    scTrack(Event.HackbountyUserClick, {
      page_name: Page.HackbountyHomePage,
      button_name: 'SubmitBounty',
    });

    router.push(`/${locale}/${appPath}/report`);
  };

  const handleViewRules = () => {
    scTrack(Event.HackbountyUserClick, {
      page_name: Page.HackbountyHomePage,
      button_name: 'ViewRules',
    });

    window.open(VIEW_RULES_LINK, '_blank');
  };

  const handleBybitLogoClick = () => {
    window.open(BYBIT_LINK, '_blank');
  };

  const handleTrackingClick = useCallback(() => {
    window.open(TRACKING_API_LINK, '_blank');
  }, []);

  if (loading) {
    return (
      // <div className="flex justify-center items-center h-[500px]">
      //   <Spin />
      // </div>
      <CommonLoading loading={loading} />
    );
  }

  return (
    <section className="md:max-w-[1200px] max-w-full md:flex-row flex-col flex justify-between items-center gap-6  mx-auto  mt-2 md:mt-9 p-4 md:p-0">
      {/* PC才显示 */}
      <div className="max-[768px]:hidden">
        <div className="flex flex-col items-start">
          <h2 className="text-white text-[40px] font-bold flex items-center gap-3">
            <BybitIcon
              onClick={handleBybitLogoClick}
              className="cursor-pointer"
            />
            <span className="w-[1px] h-8 bg-[#404347]" />
            <span>{fm('hackBounty')}</span>
          </h2>
          {/* 数据展示 */}
          <div className="flex items-center justify-between mt-7 gap-9 whitespace-nowrap">
            <div className="flex flex-col items-start gap-1">
              <span className="flex flex-row items-center gap-1 font-medium text-[#ADB1B8] text-sm">
                <span>{fm('totalBounty')}</span>
              </span>
              <span className="text-white text-[28px] font-semibold">
                $
                {formatNumber(totalStats?.totalBounty || 0, {
                  kmb: false,
                  decimal: 0,
                  roundMode: BigNumber.ROUND_DOWN,
                })}
              </span>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="flex flex-row items-center gap-1 text-[#ADB1B8] text-sm">
                <span>{fm('paidBounty')}</span>
              </span>
              <span className="text-white text-[28px] font-semibold">
                $
                {formatNumber(totalStats?.paidBounty || 0, {
                  kmb: false,
                  decimal: 0,
                  roundMode: BigNumber.ROUND_DOWN,
                })}
              </span>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-[#ADB1B8] text-sm">
                {fm('bountyHunters')}
              </span>
              <span className="text-white text-[28px] font-semibold">
                {formatNumber(totalStats?.bountyHunters || 0, {
                  kmb: false,
                  decimal: 0,
                  roundMode: BigNumber.ROUND_DOWN,
                })}
              </span>
            </div>
          </div>
          <ol className="text-[#ADB1B8] text-base mt-8 break-words">
            <li className="flex">
              <span className="mr-1">1.</span>
              <span>{fm('bountyPaidInstantlyConfirmation')}</span>
            </li>
            <li className="flex">
              <span className="mr-1">2.</span>
              <span>
                {fm('contributionPercentage')}
                <br />
                a. {fm('fundBlockerFreezerPercentage')}
                <br />
                b. {fm('fundTraceContributionPercent')}
              </span>
            </li>
          </ol>

          <div className="flex items-center mt-8 gap-6 w-full">
            <Button
              variant="primary"
              size="large"
              className="flex-1 px-10"
              onClick={handleSubmitReport}
            >
              {fm('submitBounty')}
            </Button>
            <Button
              variant="secondaryBrand"
              size="large"
              className="flex-1 px-10"
              onClick={handleViewRules}
            >
              {fm('viewRules')}
            </Button>
          </div>
        </div>
      </div>
      {/* 左边， H5才显示 */}
      <div className="min-[768px]:hidden w-full">
        <div className="flex flex-col items-start">
          <h2 className="text-white text-2xl font-bold flex items-center gap-3">
            <BybitIconSmall
              onClick={handleBybitLogoClick}
              className="cursor-pointer"
            />
            <span className="w-[1px] h-8 bg-[#404347]" />
            <span>{fm('hackBounty')}</span>
          </h2>
          {/* 数据展示 */}
          <div className="flex items-center justify-between mt-6 gap-6 whitespace-nowrap w-full">
            <div className="flex-1 flex flex-col items-start gap-1">
              <span className="flex flex-row items-center gap-1 font-medium text-[#ADB1B8] text-xs">
                <span>{fm('totalBounty')}</span>
              </span>
              <span className="text-white text-lg font-semibold">
                $
                {formatNumber(totalStats?.totalBounty || 0, {
                  kmb: false,
                  decimal: 0,
                  roundMode: BigNumber.ROUND_DOWN,
                })}
              </span>
            </div>
            <div className="flex-1 flex flex-col items-start gap-1">
              <span className="flex flex-row items-center gap-1 text-[#ADB1B8] text-xs">
                <span>{fm('paidBounty')}</span>
              </span>
              <span className="text-white text-lg font-semibold">
                $
                {formatNumber(totalStats?.paidBounty || 0, {
                  kmb: false,
                  decimal: 0,
                  roundMode: BigNumber.ROUND_DOWN,
                })}
              </span>
            </div>
            <div className="flex-1 flex flex-col items-start gap-1">
              <span className="text-[#ADB1B8] text-xs">
                {fm('bountyHunters')}
              </span>
              <span className="text-white text-lg font-semibold">
                {formatNumber(totalStats?.bountyHunters || 0, {
                  kmb: false,
                  decimal: 0,
                  roundMode: BigNumber.ROUND_DOWN,
                })}
              </span>
            </div>
          </div>
          <ol className="text-[#ADB1B8] text-sm mt-6 break-words">
            <li className="flex">
              <span className="mr-1">1.</span>
              <span>{fm('bountyPaidInstantlyConfirmation')}</span>
            </li>
            <li className="flex mt-4">
              <span className="mr-1">2.</span>
              <span>
                {fm('contributionPercentage')}
                <br />
                a. {fm('fundBlockerFreezerPercentage')}
                <br />
                b. {fm('fundTraceContributionPercent')}
              </span>
            </li>
          </ol>
        </div>
      </div>
      <div>
        <FundDistributionPieChart
          frozenAmount={totalStats?.frozenAmount}
          awaitingResponseAmount={totalStats?.awaitingResponseAmount}
          trackingAmount={totalStats?.trackingAmount}
          totalHackedAmount={totalHackedAmount.toString()}
        />
      </div>
      <div
        ref={footerActionRef}
        className={clsx(
          'md:hidden flex items-center flex-col fixed w-full bottom-0 pb-6 pt-4 bg-[#16171A] px-4 z-10 transition-all',
          y >
            document.body.offsetHeight -
              window.outerHeight -
              (footerActionRef.current?.offsetHeight ?? 0) && '-bottom-100'
        )}
      >
        <Button
          variant="primary"
          size="middle"
          block
          onClick={handleSubmitReport}
        >
          {fm('submitBounty')}
        </Button>
        <TextLink
          variant="secondary"
          className="text-base font-bold text-[#ADB1B8] mt-4"
          color="#ADB1B8"
          onClick={handleTrackingClick}
        >
          {fm('hackerAddressTrackingApi')}
        </TextLink>
      </div>
    </section>
  );
};
